import { GameState, GameEvent, CardDef, AttackGroup, CardInstance } from '../types.js';
import { getPlayerState, getOpponentSide, getInstance, getEffectivePower, cloneState } from './GameState.js';
import { koCharacter } from './Verbs.js';
import {
  hasPassive,
  fireOnDamageOpponentTriggers,
} from './AbilityProcessor.js';

/** Apply damage to a character, absorbing with armor first. Returns power damage dealt. */
export function applyDamage(inst: CardInstance, amount: number): number {
  const armorAbsorb = Math.min(inst.armor, amount);
  inst.armor -= armorAbsorb;
  const remaining = amount - armorAbsorb;
  inst.currentPower -= remaining;
  return remaining;
}

/** Check all ring characters and KO any whose effective power has dropped to 0 or below (e.g. after a passive provider was KO'd) */
export function sweepPassiveKOs(state: GameState, events: GameEvent[], catalog: Map<string, CardDef>): void {
  let found = true;
  while (found) {
    found = false;
    for (const side of ['player', 'opponent'] as const) {
      const ps = getPlayerState(state, side);
      for (const slot of ps.ring) {
        if (!slot.instanceId) continue;
        if (getEffectivePower(state, slot.instanceId, catalog) <= 0) {
          koCharacter(state, slot.instanceId, events, catalog);
          found = true; // re-sweep in case this KO removes another passive
        }
      }
    }
  }
}

export function resolveCombat(
  state: GameState,
  catalog: Map<string, CardDef>,
): { state: GameState; events: GameEvent[] } {
  // Clone defensively at entry so the caller's state is never mutated.
  // resolveCombat mutates extensively (damage application, KO sweeps,
  // setting state.combatState = null at the end), and the function's
  // return shape already advertises a returned state — so the contract
  // should match dispatch's "pure transition" pattern. Without this
  // clone, calling resolveCombat from a search loop (e.g. SearchAI via
  // replayActions) would silently corrupt the live state, nulling
  // combatState and breaking subsequent ASSIGN_BLOCKER dispatches.
  const next = cloneState(state);
  const events: GameEvent[] = [];
  const combat = next.combatState;
  if (!combat) return { state: next, events };

  const attackerSide = next.currentPlayer;
  const defenderSide = getOpponentSide(attackerSide);
  const defenderState = getPlayerState(next, defenderSide);

  // Snapshot (group attackers, blocker) pairs before iterating. As each
  // attack resolves, koCharacter() prunes KO'd attackers from
  // combat.groups and remaps combat.blockerAssignments — that's the
  // right behavior for KOs that happen *outside* combat resolution
  // (e.g. an activated ability or a reaction during counter phase),
  // but mutating the same arrays we're iterating breaks the loop:
  // dropping a group shifts every later group's index down, so the
  // for-loop would skip the next group entirely. Damage is simultaneous,
  // so every declared attack must resolve regardless of who dies in an
  // earlier group.
  const pairs = combat.groups.map((g, i) => ({
    attackerIds: [...g.attackerIds],
    united: g.united,
    blockerId: combat.blockerAssignments.get(i) ?? null,
  }));
  // Track which pair delivered the killing blow — the lead attacker of
  // the first pair whose resolution brings defender HP to 0 or below.
  // Earlier pairs may have dealt non-lethal damage (or no HP damage at
  // all if blocked without piercing); we want the attacker that
  // actually finished the opponent off, not just the first one to
  // attack this turn. Tracked here in the resolver loop because by the
  // end of all pairs we've lost the per-pair attribution.
  let killingPairLeadAttackerId: string | null = null;
  const hpWasAlive = defenderState.hp > 0;
  for (const pair of pairs) {
    if (pair.attackerIds.length === 0) continue;
    if (pair.united && pair.attackerIds.length >= 2) {
      resolveUnitedGroup(
        next,
        { united: pair.united, attackerIds: pair.attackerIds },
        pair.blockerId,
        events,
        catalog,
      );
    } else {
      resolveIndividualAttack(next, pair.attackerIds[0], pair.blockerId, events, catalog);
    }
    if (hpWasAlive && defenderState.hp <= 0 && !killingPairLeadAttackerId) {
      killingPairLeadAttackerId = pair.attackerIds[0];
    }
  }

  // Sweep for characters that should be KO'd due to lost passive buffs
  sweepPassiveKOs(next, events, catalog);

  // Check win conditions after combat damage
  if (defenderState.hp <= 0) {
    next.winner = attackerSide;
    next.winReason = 'hp';
    const responsibleCardId = killingPairLeadAttackerId
      ? next.cardInstances.get(killingPairLeadAttackerId)?.cardId ?? null
      : null;
    events.push({ type: 'game-over', winner: attackerSide, reason: 'hp', responsibleCardId });
  }

  // Combat is done — clear combatState so phase-machine helpers
  // (shouldAutoAdvance, etc.) treat the counter phase as resolved.
  next.combatState = null;

  return { state: next, events };
}

function resolveUnitedGroup(
  state: GameState,
  group: AttackGroup,
  blockerId: string | null,
  events: GameEvent[],
  catalog: Map<string, CardDef>,
): void {
  const defenderSide = getOpponentSide(state.currentPlayer);
  const defenderState = getPlayerState(state, defenderSide);

  // Calculate combined attack power (power only, armor doesn't contribute)
  let totalAttackPower = 0;
  for (const attackerId of group.attackerIds) {
    totalAttackPower += getEffectivePower(state, attackerId, catalog);
  }

  if (blockerId) {
    const blockerInst = getInstance(state, blockerId);
    const blockerPower = getEffectivePower(state, blockerId, catalog);
    const blockerArmor = blockerInst.armor;

    // Blocker damages each attacker sequentially. We defer KOs until
    // after both sides have taken damage — otherwise an attacker
    // who's also a passive provider (e.g. intimidate) would have its
    // effect lifted from the blocker the instant it died, and the
    // blocker could survive a damage exchange that should have
    // killed it under the rules-as-written ("damage resolves
    // simultaneously; everyone who hit zero at the same instant
    // dies together").
    let remainingBlockerDamage = blockerPower;
    for (const attackerId of group.attackerIds) {
      if (remainingBlockerDamage <= 0) break;
      const attackerInst = getInstance(state, attackerId);
      if (getEffectivePower(state, attackerId, catalog) <= 0) continue;
      const maxDamage = attackerInst.armor + getEffectivePower(state, attackerId, catalog);
      const damageAttempt = Math.min(remainingBlockerDamage, maxDamage);
      applyDamage(attackerInst, damageAttempt);
      remainingBlockerDamage -= damageAttempt;
      events.push({ type: 'combat-damage', attackerInstanceId: blockerId, defenderInstanceId: attackerId, damage: damageAttempt });
    }

    // United attack damages blocker with full combined power. Marked
    // `united: true` so the voice-line router can pick this as the
    // primary event (the counter-damage events from the blocker fire
    // first chronologically; without the flag the router would
    // misidentify the blocker as the attacker).
    events.push({ type: 'combat-damage', attackerInstanceId: group.attackerIds[0], defenderInstanceId: blockerId, damage: totalAttackPower, united: true });
    applyDamage(blockerInst, totalAttackPower);

    // Snapshot effective powers NOW — every participant in this
    // exchange is still on the board, so any passives in play apply
    // at their pre-KO strength. KO based on the snapshot regardless
    // of any cascading passive losses that subsequent koCharacter
    // calls in this loop would cause.
    const dyingAttackers = group.attackerIds.filter(
      id => getEffectivePower(state, id, catalog) <= 0,
    );
    const blockerDies = getEffectivePower(state, blockerId, catalog) <= 0;
    // kill-credit emitted just before each KO so the UI can route the
    // `kill_other` voice trigger to the killer. The blocker is the
    // killer of any dying attacker; the primary attacker of the united
    // group is credited with the blocker's death.
    for (const id of dyingAttackers) {
      events.push({ type: 'kill-credit', killerInstanceId: blockerId, victimInstanceId: id });
      koCharacter(state, id, events, catalog);
    }
    if (blockerDies) {
      events.push({ type: 'kill-credit', killerInstanceId: group.attackerIds[0], victimInstanceId: blockerId });
      koCharacter(state, blockerId, events, catalog);
    }

    // Trample: if combined attack > blocker effective HP (power + armor), excess goes to player HP
    const blockerTotalHp = blockerPower + blockerArmor;
    if (totalAttackPower > blockerTotalHp) {
      const piercingDamage = totalAttackPower - blockerTotalHp;
      const oldHp = defenderState.hp;
      defenderState.hp -= piercingDamage;
      events.push({ type: 'hp-changed', player: defenderSide, oldHp, newHp: defenderState.hp, amount: -piercingDamage });
      applyLeechHeal(state, group.attackerIds, piercingDamage, events, catalog);
      events.push(...fireOnDamageOpponentTriggers(state, group.attackerIds, piercingDamage, catalog));
    }
  } else {
    // Unblocked united attack — all damage goes to HP. Emit a
    // combat-damage event with defender=null (matches the unblocked
    // individual-attack pattern) so the lead attacker gets its
    // anim-attack beat AND so the voice-line router can identify
    // this as a combat exchange and fire combat_engage / kill_opponent
    // bubbles on the lead. Marked `united: true` for the higher
    // probability gate.
    events.push({ type: 'combat-damage', attackerInstanceId: group.attackerIds[0], defenderInstanceId: null, damage: totalAttackPower, united: true });
    const oldHp = defenderState.hp;
    defenderState.hp -= totalAttackPower;
    events.push({ type: 'hp-changed', player: defenderSide, oldHp, newHp: defenderState.hp, amount: -totalAttackPower });
    applyLeechHeal(state, group.attackerIds, totalAttackPower, events, catalog);
    events.push(...fireOnDamageOpponentTriggers(state, group.attackerIds, totalAttackPower, catalog));
  }
}

function resolveIndividualAttack(
  state: GameState,
  attackerId: string,
  blockerId: string | null,
  events: GameEvent[],
  catalog: Map<string, CardDef>,
): void {
  const defenderSide = getOpponentSide(state.currentPlayer);
  const defenderState = getPlayerState(state, defenderSide);
  const attackerInst = getInstance(state, attackerId);
  if (getEffectivePower(state, attackerId, catalog) <= 0) return;

  if (blockerId) {
    const blockerInst = getInstance(state, blockerId);
    const attackerPower = getEffectivePower(state, attackerId, catalog);
    const blockerPower = getEffectivePower(state, blockerId, catalog);
    const blockerArmor = blockerInst.armor;

    // Simultaneous damage — individual attacks only pierce when the attacker has passive-pierce
    applyDamage(attackerInst, blockerPower);
    applyDamage(blockerInst, attackerPower);

    events.push({ type: 'combat-damage', attackerInstanceId: attackerId, defenderInstanceId: blockerId, damage: attackerPower });
    events.push({ type: 'combat-damage', attackerInstanceId: blockerId, defenderInstanceId: attackerId, damage: blockerPower });

    if (hasPassive(state, attackerId, 'passive-pierce', catalog)) {
      const blockerTotalHp = blockerPower + blockerArmor;
      if (attackerPower > blockerTotalHp) {
        const piercingDamage = attackerPower - blockerTotalHp;
        // Banner the pierce only when damage actually pierced — this is the
        // moment "this character deals damage to the opponent" via pierce.
        events.push({ type: 'ability-triggered', instanceId: attackerId, abilityType: 'passive', effect: 'passive-pierce' });
        const oldHp = defenderState.hp;
        defenderState.hp -= piercingDamage;
        events.push({ type: 'hp-changed', player: defenderSide, oldHp, newHp: defenderState.hp, amount: -piercingDamage });
        applyLeechHeal(state, [attackerId], piercingDamage, events, catalog);
        events.push(...fireOnDamageOpponentTriggers(state, [attackerId], piercingDamage, catalog));
      }
    }

    // Snapshot both effective powers NOW — both characters are still
    // on the board so passives in play apply at their pre-KO
    // strength. KOing one first would lift its passive on the other
    // (e.g. attacker's intimidate ceases the instant the attacker
    // dies), and the survivor would mysteriously regain the
    // intimidate value of power and survive a damage exchange that
    // should have killed them simultaneously.
    const attackerDies = getEffectivePower(state, attackerId, catalog) <= 0;
    const blockerDies = getEffectivePower(state, blockerId, catalog) <= 0;
    // kill-credit just before each KO so the UI can route the
    // `kill_other` voice trigger to the killer (the surviving party
    // of the exchange that finished off this character).
    if (attackerDies) {
      events.push({ type: 'kill-credit', killerInstanceId: blockerId, victimInstanceId: attackerId });
      koCharacter(state, attackerId, events, catalog);
    }
    if (blockerDies) {
      events.push({ type: 'kill-credit', killerInstanceId: attackerId, victimInstanceId: blockerId });
      koCharacter(state, blockerId, events, catalog);
    }
  } else {
    // Unblocked — damage to HP
    const damage = getEffectivePower(state, attackerId, catalog);
    const oldHp = defenderState.hp;
    defenderState.hp -= damage;
    events.push({ type: 'combat-damage', attackerInstanceId: attackerId, defenderInstanceId: null, damage });
    events.push({ type: 'hp-changed', player: defenderSide, oldHp, newHp: defenderState.hp, amount: -damage });
    applyLeechHeal(state, [attackerId], damage, events, catalog);
    events.push(...fireOnDamageOpponentTriggers(state, [attackerId], damage, catalog));
  }
}

function applyLeechHeal(
  state: GameState,
  attackerIds: string[],
  hpDamage: number,
  events: GameEvent[],
  catalog: Map<string, CardDef>,
): void {
  if (hpDamage <= 0) return;
  const attackerSide = state.currentPlayer;
  const attackerState = getPlayerState(state, attackerSide);
  for (const id of attackerIds) {
    if (!hasPassive(state, id, 'passive-leech', catalog)) continue;
    // Banner before the heal so the cause-effect order ("this character's
    // leech triggers" → HP-up flyaway) reads cleanly.
    events.push({ type: 'ability-triggered', instanceId: id, abilityType: 'passive', effect: 'passive-leech' });
    const oldHp = attackerState.hp;
    attackerState.hp += hpDamage;
    events.push({ type: 'hp-changed', player: attackerSide, oldHp, newHp: attackerState.hp, amount: hpDamage });
  }
}
